import Bluebird from "bluebird";

import { ensureError } from "./misc";

export class Deferred<T = void> implements Promise<T> {
  private emitter: EventTarget;

  private promise: Promise<T>;
  private settled = false;
  declare resolveInternal: (value: T) => void;
  declare rejectInternal: (reason?: unknown) => void;

  constructor() {
    this.emitter = new EventTarget();

    this.promise = new Promise<T>((resolve, reject) => {
      this.emitter.addEventListener(
        "settled",
        (e) => {
          this.settled = true;

          if (e instanceof ResolvedEvent) {
            resolve(e.value);
          } else if (e instanceof RejectedEvent) {
            reject(ensureError(e.reason));
          } else {
            reject(new Error("Invalid event emitted. This is a bug."));
          }
        },
        { once: true },
      );
    });
  }

  readonly [Symbol.toStringTag] = "Deferred";

  then<TResult1 = void, TResult2 = never>(
    onfulfilled?:
      | ((value: T) => TResult1 | PromiseLike<TResult1>)
      | null
      | undefined,
    onrejected?: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((reason: any) => TResult2 | PromiseLike<TResult2>) | null | undefined,
  ): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((reason: any) => TResult | PromiseLike<TResult>) | null | undefined,
  ): Promise<T | TResult> {
    return this.promise.catch(onrejected);
  }
  finally(onfinally?: (() => void) | null | undefined): Promise<T> {
    return this.promise.finally(onfinally);
  }

  resolve(value: T) {
    if (this.settled) {
      throw new Error("Deferred is already settled.");
    }
    this.emitter.dispatchEvent(new ResolvedEvent(value));
  }

  reject(reason?: unknown) {
    if (this.settled) {
      throw new Error("Deferred is already settled.");
    }
    this.emitter.dispatchEvent(new RejectedEvent(reason));
  }
}

class ResolvedEvent<T> extends Event {
  constructor(public readonly value: T) {
    super("settled");
  }
}

class RejectedEvent extends Event {
  constructor(public readonly reason: unknown) {
    super("settled");
  }
}

export type PromiseQueueOptions = {
  concurrency?: number;
  requestsPerSecond?: number;
};

export class PromiseQueue {
  private activeInternal = 0;
  private lastStartTime = 0;
  private queue: Array<Deferred> = [];
  private readonly emitter = new EventTarget();
  private readonly options: PromiseQueueOptions;

  get active() {
    return this.activeInternal;
  }

  constructor(optionsOrConcurrency: PromiseQueueOptions | number = 1) {
    if (typeof optionsOrConcurrency === "number") {
      this.options = { concurrency: optionsOrConcurrency };
    } else {
      this.options = optionsOrConcurrency;
    }
  }

  add<T>(promiseFn: () => Promise<T>): Promise<T> {
    const promise = new Promise<T>((resolve, reject) => {
      const waitHandle = new Deferred();
      waitHandle.then(() => {
        promiseFn()
          .then(resolve)
          .catch(reject)
          .finally(() => {
            this.activeInternal--;
            this.worker();
          });
      });

      this.queue.push(waitHandle);
    });

    this.worker();

    return promise;
  }

  drain(timeout?: number): Promise<void> {
    if (this.queue.length === 0 && this.activeInternal === 0) {
      return Promise.resolve();
    }

    let promise = new Bluebird((resolve) => {
      this.emitter.addEventListener("drain", resolve, { once: true });
    });

    if (timeout != null) {
      promise = promise.timeout(timeout);
    }

    return promise;
  }

  private worker() {
    while (
      this.queue.length > 0 &&
      this.activeInternal < (this.options.concurrency ?? Infinity)
    ) {
      if (this.deferWorker()) {
        break;
      }

      this.lastStartTime = Date.now();
      const next = this.queue.shift()!;
      this.activeInternal++;
      next.resolve();
    }

    if (this.queue.length === 0 && this.activeInternal === 0) {
      this.emitter.dispatchEvent(new Event("drain"));
    }
  }

  private deferWorker() {
    if (this.options.requestsPerSecond == null) return false;

    const interval = 1000 / this.options.requestsPerSecond;
    const elapsed = Date.now() - this.lastStartTime;
    if (elapsed < interval) {
      setTimeout(() => this.worker(), interval - elapsed);
      return true;
    }

    return false;
  }
}
